import React, { useContext, useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import connectRoutes from 'shared/hoc/withRoutes';
import { buildingCreateCheck, buildingEditCheck, buildingViewCheck } from 'shared/api/buildings';
import { connectSideNav } from 'shared/hoc/withSideNav';
import { networkingViewCheck } from 'shared/api/networking';
import { FilterContext, prepareFilters } from 'shared/hoc/withFilters';
import { processRoutes } from 'shared/utils';
import { MODULES } from 'shared/constants';
import * as routes from 'shared/routes/building';
import BuildingSearch from './BuildingSearch';
import BuildingCreator from './BuildingCreator';
import {
  AccessControlResource,
  AvControlResource,
  DirectvPackageResource,
  FireResource,
  IntrusionResource,
  PropertyComplexResource,
  PropertyManagerResource,
  RemotePowerManagementResource,
  SatellitePkgResource,
  SurveillanceResource,
} from './BuildingResources';
import UpgradeCreator from './UpgradeCreator';
import BuildingNav from './BuildingNav';
import BuildingView from './BuildingView';
import UnitRouter from './unit/UnitRouter';
import UpgradeManager from './UpgradeManager';
import Security from './Security';
import Satellite from './Satellite';
import BuildingTickets from './BuildingTickets';
import BuildingNetworkingLinks from './BuildingNetworkingLinks';

const withSideNav = connectSideNav(MODULES.building);

const withFilters = prepareFilters(MODULES.building);

const withRoutes = connectRoutes([
  {
    RouteComponent: BuildingSearch,
    text: 'Search Buildings',
    path: routes.viewPath,
    routeToTest: buildingViewCheck,
    key: 'search_buildings',
  },
  {
    RouteComponent: BuildingCreator,
    text: 'Create Buildings',
    path: routes.createPath,
    routeToTest: buildingCreateCheck,
  },
  {
    text: 'Resources',
    routeToTest: buildingEditCheck,
    path: routes.accessControlPath,
    groupHead: routes.resourcePath,
  },
  {
    RouteComponent: AccessControlResource,
    text: 'Access Control',
    routeToTest: buildingEditCheck,
    path: routes.accessControlPath,
    group: routes.resourcePath,
  },
  {
    RouteComponent: AvControlResource,
    text: 'Av Control',
    routeToTest: buildingEditCheck,
    path: routes.avControlPath,
    group: routes.resourcePath,
  },
  {
    RouteComponent: PropertyComplexResource,
    text: 'Complexes',
    routeToTest: buildingEditCheck,
    path: routes.complexPath,
    group: routes.resourcePath,
  },
  {
    RouteComponent: DirectvPackageResource,
    text: 'DirecTV',
    routeToTest: buildingEditCheck,
    path: routes.dtvPath,
    group: routes.resourcePath,
  },
  {
    RouteComponent: FireResource,
    text: 'Fire Monitors',
    routeToTest: buildingEditCheck,
    path: routes.firePath,
    group: routes.resourcePath,
  },
  {
    RouteComponent: IntrusionResource,
    text: 'Intrusion Monitors',
    routeToTest: buildingEditCheck,
    path: routes.intrusionPath,
    group: routes.resourcePath,
  },
  {
    RouteComponent: PropertyManagerResource,
    text: 'Property Managers',
    routeToTest: buildingEditCheck,
    path: routes.managerPath,
    group: routes.resourcePath,
  },
  {
    RouteComponent: RemotePowerManagementResource,
    text: 'Remote Pwr Mgmt',
    routeToTest: buildingEditCheck,
    path: routes.remotePowerManagementPath,
    group: routes.resourcePath,
  },
  {
    RouteComponent: SatellitePkgResource,
    text: 'Satellite Packages',
    routeToTest: buildingEditCheck,
    path: routes.satellitePkgPath,
    group: routes.resourcePath,
  },
  {
    RouteComponent: SurveillanceResource,
    text: 'Surveillance',
    routeToTest: buildingEditCheck,
    path: routes.surveillancePath,
    group: routes.resourcePath,
  },
  {
    RouteComponent: UpgradeCreator,
    text: 'Upgrades',
    routeToTest: buildingEditCheck,
    path: routes.upgradeResourcePath,
    group: routes.resourcePath,
  },
  {
    // Everything below this is passed to the building router.
    // These are filtered out of the side nav because there is no 'path' key.
    // The paths are created dynamically via the getPath function.
    RouteComponent: BuildingView,
    text: 'View',
    getPath: routes.getBuildingPath,
    exact: true,
  },
  {
    RouteComponent: UnitRouter,
    text: 'Units',
    getPath: routes.getUnitsPath,
  },
  {
    RouteComponent: Satellite,
    text: 'Satellite',
    getPath: routes.getSatellitePath,
  },
  {
    RouteComponent: Security,
    text: 'Security',
    getPath: routes.getSecurityPath,
    dependsOn: 'building_security',
  },
  {
    RouteComponent: UpgradeManager,
    text: 'Upgrades',
    getPath: routes.getBuildingUpgradesPath,
    dependsOn: 'dot1x',
  },
  {
    RouteComponent: BuildingTickets,
    text: 'Tickets',
    getPath: routes.getBuildingTicketsPath,
    exact: true,
    key: 'building_tickets',
  },
  {
    RouteComponent: BuildingNetworkingLinks,
    text: 'Networking',
    routeToTest: networkingViewCheck,
    getPath: routes.getBuildingLinksPath,
  },
]);

/**
 * @param {Array<Object>} enabledRoutes - Array of enabled route objects
 */
function Building({ enabledRoutes }) {
  const {
    savedFilters,
    getFavoriteFilters,
  } = useContext(FilterContext);

  useEffect(() => {
    if (!savedFilters) {
      getFavoriteFilters();
    }
  }, [savedFilters, getFavoriteFilters]);

  // Routes that depend on building id.
  const initialBuildingNavRoutes = [];
  const initialStaticRoutes = [];

  const { staticRoutes, navRoutes } = processRoutes(
    enabledRoutes,
    initialStaticRoutes,
    initialBuildingNavRoutes,
    savedFilters,
  );

  return (
    <Switch>
      {staticRoutes.map((route) => (
        <Route
          render={() => <route.RouteComponent filterKey={route.key || ''} savedFilters={route.filters || ''} path={route.path} />}
          exact
          key={route.path}
          path={route.path}
        />
      ))}
      <Route
        path={`${routes.viewPath}/:routeId`}
        render={() => <BuildingNav enabledRoutes={navRoutes} />}
      />
    </Switch>
  );
}

export default {
  RouteComponent: withRoutes(withFilters(withSideNav(Building))),
  module: 'building',
  name: 'Buildings',
  path: routes.viewPath,
  rootPath: routes.rootPath,
  routeToTest: buildingViewCheck,
};
